import {
  canDeleteDocument,
  canVoidDocument,
  capitalise,
  documentKindLabel,
  cents,
  documentLinkOpens,
  formatMoney,
  jobDocuments,
  type Job,
  type JobFileEntry,
  type ProposalEntry,
} from "@roof-hawk/core";
import { useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { CertificateButton, CertificateRow } from "./CertificateRow";
import { ChangeOrderRecordModal } from "./ChangeOrderRecordModal";
import { DocumentRecordModal } from "./DocumentRecordModal";
import { fmtDateTime } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { emailSignedCopy } from "../../lib/signedCopy";
import {
  deleteJobDocument,
  fetchJobCertificates,
  fetchJobChangeOrderDocuments,
  fetchJobDocuments,
  fetchJobFiles,
  signJobFile,
  uploadJobDocument,
  voidProposal,
  type JobDocument,
} from "../../lib/queries";
import { useSession } from "../../session";

/**
 * Docs: every copy of this job that has ever left the building.
 *
 * Jonathon, 2026-08-13: "docs section should reflect any presented, sent, or
 * signed contract."
 *
 * Which is the same list, at different points in its life. A presented document
 * is one row from the moment the rep presses Present until the customer signs
 * it: it is sent, then opened, then accepted, and the link never changes. So
 * this is not three lists — it is one, showing where each document got to.
 *
 * A voided one stays. It is the answer to "what did you send me on Tuesday?",
 * and the whole reason a presented document is frozen (ADR 0032).
 *
 * Voiding is done from here, because this is the list of what is live. It kills
 * the link, stands the estimate back down from approved, and is what has to
 * happen before a sent estimate can be cleared off the builder (ADR 0047).
 */
export function DocsPanel({ job }: { job: Job }) {
  const { profileId } = useSession();
  const documents = useAsync(async () => {
    const [proposals, files, changeOrders, certificates] = await Promise.all([
      fetchJobDocuments(job.id),
      fetchJobFiles(job.id),
      // The change orders too. A signed one moves what the customer owes, and
      // until 2026-09-23 it appeared on this list nowhere (ADR 0205).
      fetchJobChangeOrderDocuments(job.id),
      // And the certificates of completion, made from here (ADR 0308).
      fetchJobCertificates(job.id),
    ]);
    return jobDocuments(proposals, files, changeOrders, certificates);
  }, [job.id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** What just happened, when it is worth saying so. */
  const [message, setMessage] = useState<string | null>(null);
  /** The document being read back, if somebody has opened one. */
  const [reading, setReading] = useState<string | null>(null);
  /** The change order being read back, if somebody has opened one. */
  const [readingChange, setReadingChange] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const rows = documents.data ?? [];

  /**
   * Anything, not just a PDF.
   *
   * Jonathon asked about PDFs because that is what a measurement report and a
   * signed contract are, but a job also accumulates a photograph of a permit
   * and a spreadsheet from an adjuster. Refusing those would send somebody
   * back to email for the one document they needed here.
   */
  async function upload(file: File | undefined) {
    if (!file) return;
    setBusy("upload");
    setError(null);
    try {
      await uploadJobDocument(job.id, file, profileId ?? null);
      await documents.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
      if (picker.current) picker.current.value = "";
    }
  }

  /** A private bucket, so the link is signed at the moment it is asked for. */
  async function open(entry: JobFileEntry) {
    setBusy(entry.id);
    setError(null);
    try {
      window.open(await signJobFile(entry), "_blank", "noreferrer");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(entry: JobFileEntry) {
    if (
      !window.confirm(
        `${t('Delete "{title}"?', { title: entry.title })}\n\n${t("The file is removed for good. This cannot be undone.")}`,
      )
    )
      return;
    setBusy(entry.id);
    setError(null);
    try {
      await deleteJobDocument(entry);
      await documents.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Send the customer their copy of what they signed.
   *
   * The Edge Function does this by itself at signing (ADR 0122). This button
   * is for the two cases it cannot cover: a contract signed before that was
   * built, and one whose copy did not go because a mailbox was not connected
   * that day. It is a letter to a customer, so it says who it reached.
   */
  async function sendCopy(row: ProposalEntry) {
    setBusy(row.id);
    setError(null);
    setMessage(null);
    try {
      const sent = await emailSignedCopy({
        proposalId: row.id,
        job,
        senderProfileId: profileId ?? null,
      });
      // It went either way; a warning means the row was not told, so the
      // "no copy sent" beside it is not to be believed.
      setMessage(
        sent.warning
          ? `${t("Their copy went to {to}.", { to: sent.to })} ${sent.warning}`
          : t("Their copy is on its way to {to}.", { to: sent.to }),
      );
      await documents.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function withdraw(row: ProposalEntry) {
    // A signature is a different conversation from an unopened link, so it is a
    // different question. Neither is undoable: voiding is one-way and the
    // remedy is a fresh document.
    const title = row.title ?? t("this document");
    const warning = row.signer_name
      ? `${t('{name} has signed "{title}".', { name: row.signer_name, title })}\n\n${t("Voiding it takes away its force — the signed record is kept, but the estimate stands back down and a new document has to be sent.")}\n\n${t("Void it anyway?")}`
      : `${t('Void "{title}"?', { title })}\n\n${t("The link stops working for the customer. This cannot be undone — present the estimate again to send a fresh one.")}`;
    if (!window.confirm(warning)) return;

    setBusy(row.id);
    setError(null);
    try {
      await voidProposal(row.id);
      await documents.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  // Blank only on the first load. A reload keeps the card mounted, because a
  // modal opened from it may be mid-way through: on a job with nothing else in
  // Docs, the certificate's Create reloads this list and unmounted its own
  // "Ready to sign" screen before anyone saw it (ADR 0308).
  if (documents.loading && documents.data === null)
    return <div className="block" />;

  return (
    <div className="block">
      {reading ? (
        <DocumentRecordModal
          proposalId={reading}
          onClose={() => setReading(null)}
        />
      ) : null}
      {readingChange ? (
        <ChangeOrderRecordModal
          documentId={readingChange}
          onClose={() => setReadingChange(null)}
        />
      ) : null}
      <h2>{t("Docs")}</h2>
      {/* The card is the place documents go, so it is the place they can be
            put. A hidden input rather than a drop zone: a rep is holding a
            phone, and "choose a file" is the control a phone has. */}
      <input
        ref={picker}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => void upload(e.target.files?.[0])}
      />
      <button
        className="btn-primary"
        disabled={busy === "upload"}
        onClick={() => picker.current?.click()}
      >
        {busy === "upload" ? t("Uploading…") : t("Upload a document")}
      </button>{" "}
      {/* The certificate of completion is made from here, because this is
          where it ends up (ADR 0308). */}
      <CertificateButton job={job} onCreated={() => documents.reload()} />
      <ErrorNote error={documents.error ?? error} />
      {message && <div className="muted">{message}</div>}
      {rows.length === 0 ? (
        <div className="stub">
          <b>{t("Nothing on this job yet")}</b>
          {t(
            "Everything this job has on paper lands here — what was presented to the customer and what they signed, the measurement report, anything emailed out, and anything you upload.",
          )}
        </div>
      ) : (
        /* The buttons sit right after the name, not at the far end of the
           row. At the far end, on a phone, every Open was off the right of the
           screen and had to be scrolled to — Jonathon, 2026-10-15: "the buttons
           to view documents need to be easier to access and use instead of
           having to scroll over." On a phone each row is also laid out as a
           card (`.jobdocs` in the styles), so nothing scrolls sideways at
           all. */
        <div className="ewrap">
          <table className="etable jobdocs">
            <thead>
              <tr>
                <th>{t("Document")}</th>
                <th />
                <th>{t("State")}</th>
                <th>{t("Sent")}</th>
                <th>{t("Signed")}</th>
                <th>{t("Signed by")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) =>
                row.type === "proposal" ? (
                  <tr key={row.id}>
                    <td className="dname">
                      {row.title ?? t("Estimate")}
                      {row.revision > 1 && (
                        <span className="na-hint">
                          {" "}
                          · {t("revision {n}", { n: row.revision })}
                        </span>
                      )}
                    </td>
                    <td className="dacts">
                      {/* The customer's own copy, opened here rather than
                          through their link.

                          Their link is one-way: once it is signed it refuses to
                          open, which is right for them and useless for us. And
                          on a document still live, opening it as staff records
                          a view the customer never made. Both problems go away
                          by reading the frozen snapshot — ADR 0032 is what
                          makes that the same document they saw. */}
                      <button
                        className="btn-small dopen"
                        onClick={() => setReading(row.id)}
                      >
                        {t("Open")}
                      </button>
                      {/* Their copy of what they signed.

                          Sent automatically at signing from 2026-09-09 (ADR
                          0122); this is how a contract signed before that, or
                          one whose letter did not go, is put right. Offered on
                          a signed document only — there is nothing to send a
                          copy of until somebody has signed one. */}
                      {row.status === "accepted" && (
                        <button
                          className="btn-small"
                          disabled={busy === row.id}
                          title={
                            row.signed_copy_sent_at
                              ? t(
                                  "Their copy went out {when}. Send it again.",
                                  {
                                    when: fmtDateTime(row.signed_copy_sent_at),
                                  },
                                )
                              : t(
                                  "Email the customer their copy of this agreement",
                                )
                          }
                          onClick={() => void sendCopy(row)}
                        >
                          {busy === row.id
                            ? t("Sending…")
                            : row.signed_copy_sent_at
                              ? t("Send copy again")
                              : t("Send their copy")}
                        </button>
                      )}
                      {/* The customer's link, for sending again — offered only
                          while it still opens for them, which is a narrower
                          question than whether it can be withdrawn. A signed
                          document can still be voided and its link is already
                          dead, so `canVoidDocument` is the wrong test here. */}
                      {documentLinkOpens(row.status, row.expires_at) && (
                        <button
                          className="btn-small"
                          title={t("Copy the link the customer opens")}
                          onClick={() =>
                            void navigator.clipboard?.writeText(
                              `${window.location.origin}/p/${row.token}`,
                            )
                          }
                        >
                          {t("Copy link")}
                        </button>
                      )}
                      {canVoidDocument(row.status) && (
                        <button
                          className="btn-small danger"
                          disabled={busy === row.id}
                          title={t(
                            "Kill this link so the estimate can be changed or cleared away",
                          )}
                          onClick={() => void withdraw(row)}
                        >
                          {busy === row.id ? t("Voiding…") : t("Void")}
                        </button>
                      )}
                    </td>
                    <td>
                      <span className={`docstate ${row.status}`}>
                        {t(DOC_STATE[row.status] ?? capitalise(row.status))}
                      </span>
                    </td>
                    <td className="sub" data-label={t("Sent")}>
                      {row.sent_at ? fmtDateTime(row.sent_at) : "—"}
                    </td>
                    <td className="sub" data-label={t("Signed")}>
                      {row.accepted_at ? fmtDateTime(row.accepted_at) : "—"}
                    </td>
                    <td className="sub" data-label={t("Signed by")}>
                      {row.signer_name ?? "—"}
                      {/* Silence about a copy nobody received is how this went
                          unnoticed in the first place. */}
                      {row.status === "accepted" &&
                        !row.signed_copy_sent_at && (
                          <span className="na-hint">
                            {" "}
                            · {t("no copy sent")}
                            {row.signed_copy_error
                              ? ` (${row.signed_copy_error})`
                              : ""}
                          </span>
                        )}
                    </td>
                  </tr>
                ) : row.type === "certificate" ? (
                  <CertificateRow
                    key={row.id}
                    row={row}
                    onChanged={() => documents.reload()}
                    onError={setError}
                  />
                ) : row.type === "change_order" ? (
                  <tr key={row.id}>
                    <td className="dname">
                      {t("Change order {n}", { n: row.number })}
                      {row.change_total != null && row.change_total > 0 && (
                        <span className="na-hint">
                          {" "}
                          · {formatMoney(cents(row.change_total))}
                        </span>
                      )}
                    </td>
                    <td className="dacts">
                      {/* The frozen snapshot, not the customer's link — the
                          same reasoning as a proposal's Open two branches up:
                          their link refuses once it is signed, and on a live
                          one it would record a view they never made. */}
                      <button
                        className="btn-small dopen"
                        onClick={() => setReadingChange(row.id)}
                      >
                        {t("Open")}
                      </button>
                      {documentLinkOpens(row.status, row.expires_at) && (
                        <button
                          className="btn-small"
                          title={t("Copy the link the customer opens")}
                          onClick={() =>
                            void navigator.clipboard?.writeText(
                              `${window.location.origin}/c/${row.token}`,
                            )
                          }
                        >
                          {t("Copy link")}
                        </button>
                      )}
                    </td>
                    <td>
                      <span className={`docstate ${row.status}`}>
                        {t(DOC_STATE[row.status] ?? capitalise(row.status))}
                      </span>
                    </td>
                    <td className="sub" data-label={t("Sent")}>
                      {row.sent_at ? fmtDateTime(row.sent_at) : "—"}
                    </td>
                    <td className="sub" data-label={t("Signed")}>
                      {row.accepted_at ? fmtDateTime(row.accepted_at) : "—"}
                    </td>
                    <td className="sub" data-label={t("Signed by")}>
                      {row.signer_name ?? "—"}
                    </td>
                  </tr>
                ) : (
                  <tr key={row.id}>
                    <td className="dname">{row.title}</td>
                    <td className="dacts">
                      <button
                        className="btn-small dopen"
                        disabled={busy === row.id}
                        onClick={() => void open(row)}
                      >
                        {busy === row.id ? t("Opening…") : t("Open ↗")}
                      </button>
                      {canDeleteDocument(row) && (
                        <button
                          className="btn-small danger"
                          disabled={busy === row.id}
                          title={t("Remove this upload from the job")}
                          onClick={() => void remove(row)}
                        >
                          {t("Delete")}
                        </button>
                      )}
                    </td>
                    <td>
                      <span className="docstate file">
                        {t(documentKindLabel(row.kind))}
                      </span>
                    </td>
                    {/* A file is filed under the day it went out, or the day it
                        arrived if it never did. Nothing is ever signed, so that
                        column carries who it was emailed to instead — which is
                        the same question one column further along. On a phone
                        the empty "Signed" is left out rather than labelled. */}
                    <td className="sub" data-label={t("Date")}>
                      {fmtDateTime(row.at)}
                    </td>
                    <td className="sub dempty">—</td>
                    <td className="sub">
                      {row.emailed_to
                        ? t("Emailed {to}", { to: row.emailed_to })
                        : "—"}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const DOC_STATE: Record<string, string> = {
  draft: "Draft",
  sent: "Sent, not opened",
  viewed: "Opened",
  accepted: "Signed",
  declined: "Declined",
  expired: "Expired",
  void: "Voided",
};

export type { JobDocument };
